// src/Views/OrdersView.js
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../contexts/AuthContext';
import { getFulfillmentStatusLabel } from '../constants/fulfillmentStatus';
import LogInOrSignUpView from './LogInOrSignUpView';
import {
  MAX_CONTENT_WIDTH,
  BORDER_RADIUS,
  SPACING_ELEMENT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
  SPACING_SECTION,
} from '../constants/layout';
import {
  FONT_TITLE2,
  FONT_TITLE2_MOBILE,
  FONT_BODY,
  FONT_SUBHEADLINE,
  FONT_CAPTION2,
} from '../constants/typography';
import ContentUnavailableView from './ContentUnavailableView';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { formatPrice } from '../utils/priceFormatter';
import { normalizeOrderAmounts } from '../utils/orderAmounts';
import { PAYMENT_STATUS } from '../constants/paymentStatus';
import { AUTH_ACTION_LABEL } from '../constants/settings';

function OrdersView() {
  const { user, loading: authLoading } = useContext(AuthContext);
  const { showPrices } = useDisplayPreferences();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const fetchOrders = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setFetchError(null);
    try {
      const ordersQuery = query(
        collection(db, 'orders'),
        where('userID', '==', user.uid)
      );

      const snapshot = await getDocs(ordersQuery);
      const ordersData = snapshot.docs.map(doc => {
        const data = doc.data();
        const amounts = normalizeOrderAmounts(data);
        return {
          // Firestore's document ID is the lookup authority. V2 persists
          // id/orderID aliases, but navigation must never trust a divergent field.
          id: doc.id,
          orderNumber: data.orderNumber,
          timestamp: data.timestamp?.toDate(),
          items: data.items || [],
          currency: data.currency || 'USD',
          statuses: (data.statuses || []).map(status => ({
            id: status.id,
            date: status.date?.toDate ? status.date.toDate()
              : status.date instanceof Date ? status.date
              : typeof status.date === 'string' ? new Date(status.date)
              : new Date(),
            status: status.status,
          })),
          paymentStatus: data.paymentStatus || null,
          subtotalAmount: amounts.subtotalAmount,
          taxAmount: amounts.taxAmount,
          totalAmount: amounts.totalAmount,
        };
      });

      // Sort by timestamp, newest first
      ordersData.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setFetchError(error.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [fetchOrders, user]);

  const getStatusDisplay = (statusString) => {
    return getFulfillmentStatusLabel(statusString);
  };

  const getMostRecentStatus = (order) => {
    if (!order.statuses || order.statuses.length === 0) return null;
    return order.statuses.reduce((latest, current) =>
      current.date > latest.date ? current : latest
    );
  };

  const getOrderTotal = (order) => {
    if (order.totalAmount != null) {
      return order.totalAmount;
    }
    if (order.subtotalAmount != null && order.taxAmount != null) {
      return order.subtotalAmount + order.taxAmount;
    }
    // Legacy fallback: line total includes base price and selected options.
    return order.items.reduce((total, item) => {
      if (Number.isSafeInteger(item.totalAmount) && item.totalAmount >= 0) {
        return total + item.totalAmount;
      }
      const optionAmount = (item.selectedOptions || []).reduce(
        (sum, option) => sum + (option.unitAmount || 0),
        0,
      );
      return total + ((item.unitAmount || 0) + optionAmount) * (item.quantity || 0);
    }, 0);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  const renderOrderRow = (order) => {
    const recentStatus = getMostRecentStatus(order);
    const total = getOrderTotal(order);
    const itemCount = order.items.reduce(
      (count, item) => count + (Number.isSafeInteger(item.quantity) ? item.quantity : 0),
      0,
    );
    const paymentInfo = order.paymentStatus && order.paymentStatus !== 'paid'
      ? PAYMENT_STATUS[order.paymentStatus] || null
      : null;

    // Build subtitle: "May 13, 2025 · 3 items · Confirmed"
    const subtitleParts = [];
    if (order.timestamp) subtitleParts.push(formatDate(order.timestamp));
    subtitleParts.push(`${itemCount} ${itemCount === 1 ? 'item' : 'items'}`);
    if (recentStatus) subtitleParts.push(getStatusDisplay(recentStatus.status));
    const subtitle = subtitleParts.join(' \u00B7 ');

    return (
      <Link
        key={order.id}
        to={`/orderhistory/${order.id}`}
        className="order-row"
      >
        <div className="order-row-left">
          <div className="order-row-title-line">
            <span className="order-row-number">
              Order #{String(order.orderNumber).padStart(3, '0')}
            </span>
            {paymentInfo && (
              <span
                className="payment-badge"
                style={{
                  color: paymentInfo.color,
                  backgroundColor: `${paymentInfo.color}1F`,
                }}
              >
                {paymentInfo.label}
              </span>
            )}
          </div>
          <div className="order-row-subtitle">{subtitle}</div>
        </div>
        {showPrices && (
          <div className="order-row-total">
            {formatPrice(total, order.currency)}
          </div>
        )}
      </Link>
    );
  };

  if (authLoading) {
    return (
      <div className="orders-container loading">
        <div className="loading-spinner"></div>
        <style jsx>{`
          .orders-container.loading {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 400px;
          }

          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid var(--color-border);
            border-top-color: var(--color-accent);
            border-radius: 50%;
            animation: spin 0.6s linear infinite;
          }

          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <ContentUnavailableView
          icon="receipt_long"
          title={`${AUTH_ACTION_LABEL} to View Orders`}
          description={`${AUTH_ACTION_LABEL} to view your order history.`}
          actions={[{ label: AUTH_ACTION_LABEL, onClick: () => setShowAuth(true) }]}
        />

        {showAuth && <LogInOrSignUpView onClose={() => setShowAuth(false)} />}
      </>
    );
  }

  // Error loading orders
  if (fetchError) {
    return (
      <ContentUnavailableView
        icon="error"
        title="Unable to Load Orders"
        description={fetchError}
        actions={[{ label: 'Retry', onClick: () => { setFetchError(null); fetchOrders(); } }]}
      />
    );
  }

  // User is signed in
  if (!loading && orders.length === 0) {
    return (
      <ContentUnavailableView
        icon="history"
        title="No Transactions"
        description="Your transaction history is empty."
      />
    );
  }

  return (
    <div className="orders-container">
      <div className="orders-header">
        <h1 className="section-title">Order History</h1>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      {!loading && orders.length > 0 && (
        <div className="orders-list">
          {orders.map(renderOrderRow)}
        </div>
      )}

      <style jsx>{`
        .orders-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_CARD_BODY} ${SPACING_BUTTON} ${SPACING_SECTION};
          position: relative;
          min-height: calc(100vh - 200px);
        }

        .orders-header {
          margin-bottom: ${SPACING_CARD};
        }

        .section-title {
          font-size: ${FONT_TITLE2};
          font-weight: 600;
          margin: 0;
          color: var(--color-text-primary);
        }

        .loading-overlay {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background-color: var(--color-surface);
          border-radius: ${BORDER_RADIUS};
          padding: ${SPACING_CARD};
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--color-border);
          border-top-color: var(--color-accent);
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .orders-list {
          display: flex;
          flex-direction: column;
          background-color: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          overflow: hidden;
        }

        .order-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: ${SPACING_BUTTON} ${SPACING_CARD};
          text-decoration: none;
          color: inherit;
          transition: background-color 0.15s ease;
          border-bottom: 1px solid var(--color-border);
        }

        .order-row:last-child {
          border-bottom: none;
        }

        .order-row:hover {
          background-color: var(--color-surface-muted, rgba(0, 0, 0, 0.02));
        }

        .order-row-left {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
        }

        .order-row-title-line {
          display: flex;
          align-items: center;
          gap: ${SPACING_ELEMENT};
        }

        .order-row-number {
          font-size: ${FONT_BODY};
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .payment-badge {
          font-size: ${FONT_CAPTION2};
          font-weight: 600;
          padding: 2px ${SPACING_ELEMENT};
          border-radius: 999px;
          white-space: nowrap;
        }

        .order-row-subtitle {
          font-size: ${FONT_SUBHEADLINE};
          color: var(--color-text-secondary);
        }

        .order-row-total {
          font-size: ${FONT_BODY};
          font-weight: 600;
          color: var(--color-text-primary);
          flex-shrink: 0;
          margin-left: ${SPACING_BUTTON};
        }

        @media (max-width: 768px) {
          .section-title {
            font-size: ${FONT_TITLE2_MOBILE};
          }

          .order-row {
            padding: ${SPACING_BUTTON} ${SPACING_BUTTON};
          }
        }
      `}</style>
    </div>
  );
}

export default OrdersView;
